"use client";

import React from "react";
import { SectionHeader, CTAButton, ContentCard, type ImageComponent } from "fansunited-sports-ui";
import { FillImage } from "@/components/ui/FillImage";
import { MediaVariantsProvider } from "@/components/ui/MediaVariantsProvider";
import { getPostImageUrl } from "@/components/ui/PostImage";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import type { Post } from "@/payload-types";

export interface ArticlePyramidProps {
    articles: Post[];
    /** Hero image is the page's LCP candidate: eager load + `fetchpriority="high"`. Only the first hero on a page. */
    priority?: boolean;
    /** `sizes` for the hero image — the pyramid's rendered width (full-width by default). */
    heroSizes?: string;
}

/**
 * ArticlePyramid - Article section with 1-2-3 pyramid layout pattern.
 * First row: 1 hero article
 * Second row: 2 articles
 * Third row: 3 articles
 */
export const ArticlePyramid: React.FC<ArticlePyramidProps> = ({ articles, priority = false, heroSizes = "100vw" }) => {
    const labels = useLabels();
    const nav = useNavigation();
    const hasArticles = articles.length > 0;

    // Article layout: 1 hero, 2 second row, 3 third row
    const heroArticle = articles[0];
    const secondRowArticles = articles.slice(1, 3);
    const thirdRowArticles = articles.slice(3, 6);

    return (
        <MediaVariantsProvider media={articles.map((post) => post.heroImage)}>
            <section>
                {/* Hero Story */}
                {heroArticle && (
                    <div className="mb-2">
                        <MediaVariantsProvider media={heroArticle.heroImage} sizes={heroSizes} priority={priority}>
                            <ContentCard
                                content={{
                                    id: String(heroArticle.id),
                                    type: heroArticle.type === "video" ? "video" : "article",
                                    link: nav.post(heroArticle),
                                    title: heroArticle.title,
                                    image: {
                                        url: getPostImageUrl(heroArticle, "xlarge"),
                                        alt: heroArticle.title,
                                    },
                                    gradient:
                                        "linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.5) 30%, transparent 50%)",
                                }}
                                variant="hero"
                                ImageComponent={FillImage as ImageComponent}
                                styles={{
                                    shadow: "none",
                                    mainIconSize: "lg",
                                    typography: {
                                        title: "text-lg sm:text-xl md:text-3xl font-bold mb-0! leading-6 md:leading-8",
                                    },
                                }}
                            />
                        </MediaVariantsProvider>
                    </div>
                )}

                {/* Second Row - 2 Articles */}
                {secondRowArticles.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                        {secondRowArticles.map((post) => (
                            <ContentCard
                                key={post.id}
                                content={{
                                    id: String(post.id),
                                    type: post.type === "video" ? "video" : "article",
                                    link: nav.post(post),
                                    title: post.title,
                                    image: {
                                        url: getPostImageUrl(post, "large"),
                                        alt: post.title,
                                    },
                                    gradient:
                                        "linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.5) 30%, transparent 50%)",
                                }}
                                variant="hero"
                                ImageComponent={FillImage as ImageComponent}
                                styles={{
                                    shadow: "none",
                                    typography: {
                                        title: "text-lg font-bold mb-0! leading-6",
                                    },
                                }}
                            />
                        ))}
                    </div>
                )}

                {/* Third Row - 3 Articles */}
                {thirdRowArticles.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        {thirdRowArticles.map((post) => (
                            <ContentCard
                                key={post.id}
                                content={{
                                    id: String(post.id),
                                    type: post.type === "video" ? "video" : "article",
                                    link: nav.post(post),
                                    title: post.title,
                                    image: {
                                        url: getPostImageUrl(post, "medium"),
                                        alt: post.title,
                                    },
                                    gradient:
                                        "linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.5) 30%, transparent 50%)",
                                }}
                                variant="hero"
                                ImageComponent={FillImage as ImageComponent}
                                styles={{
                                    shadow: "none",
                                    typography: {
                                        title: "text-lg md:text-base font-bold mb-0! leading-tight",
                                    },
                                }}
                            />
                        ))}
                    </div>
                )}

                {!hasArticles && (
                    <div className="text-center py-8 text-muted-foreground">{labels.homePage.noNewsAvailable}</div>
                )}
            </section>
        </MediaVariantsProvider>
    );
};
